import { Link, router, usePage } from '@inertiajs/react';
import { Heart, Home, Package, Settings, UserRound } from 'lucide-react';
import { motion } from 'motion/react';
import StoreLayout from '@/theme2/layouts/StoreLayout';
import { cn } from '@/theme2/lib/utils';

const items = [
    ['Dashboard', '/account', Home],
    ['Profile', '/settings/profile', UserRound],
    ['Orders', '/account/orders', Package],
    ['Wishlist', '/wishlist', Heart],
    ['Settings', '/settings/appearance', Settings],
];

export default function CustomerLayout({ children }) {
    const { auth, frontendTheme } = usePage().props;
    const path = typeof window === 'undefined' ? '' : window.location.pathname;
    const initials = auth.user.name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0])
        .join('');

    return (
        <StoreLayout>
            <div
                className="py-8 text-foreground sm:py-12 lg:py-16"
                data-frontend-theme={frontendTheme?.name ?? 'theme2'}
            >
                <div className="mx-auto w-full max-w-[1440px] px-4 sm:px-6 lg:px-8">
                    <section className="mb-8 overflow-hidden rounded-[2rem] border border-rose-100 bg-[linear-gradient(135deg,#f51b75_0%,#d80b67_100%)] p-7 text-white shadow-[0_24px_80px_rgba(245,27,117,0.22)] sm:p-10">
                        <div className="flex flex-col justify-between gap-7 sm:flex-row sm:items-end">
                            <div className="flex items-center gap-5">
                                <div className="flex size-16 items-center justify-center rounded-full border border-white/20 bg-white/15 text-xl font-black text-white sm:size-20">
                                    {initials}
                                </div>
                                <div>
                                    <p className="text-[11px] font-black uppercase tracking-[0.25em] text-white/55">
                                        Captex Member
                                    </p>
                                    <h1 className="mt-2 text-3xl font-black uppercase tracking-tight sm:text-5xl">
                                        Hello, {auth.user.name.split(' ')[0]}
                                    </h1>
                                    <p className="mt-2 text-sm text-white/70">
                                        {auth.user.email}
                                    </p>
                                </div>
                            </div>
                            <Link
                                href="/shop"
                                className="inline-flex min-h-12 items-center justify-center rounded-full bg-white px-6 text-xs font-black uppercase tracking-[0.14em] text-slate-900 transition hover:-translate-y-0.5 hover:bg-[#fff3f7]"
                            >
                                Continue shopping
                            </Link>
                        </div>
                    </section>

                    <div className="grid gap-7 lg:grid-cols-[17rem_1fr]">
                        <aside className="lg:sticky lg:top-36 lg:self-start">
                            <nav
                                aria-label="Customer account"
                                className="flex gap-2 overflow-x-auto rounded-[1.75rem] border border-border/60 bg-card/85 p-3 shadow-sm backdrop-blur lg:flex-col"
                            >
                                {items.map(([label, href, Icon]) => {
                                    const active =
                                        path === href ||
                                        (href.startsWith('/settings/') && path === href);

                                    return (
                                        <Link
                                            key={label}
                                            href={href}
                                        className={cn(
                                            'relative flex shrink-0 items-center gap-3 rounded-full px-4 py-3 text-sm font-semibold text-slate-500 transition hover:bg-[#fff3f7] hover:text-[#d80b67]',
                                            active &&
                                                    'bg-[#f51b75] text-white hover:bg-[#f51b75] hover:text-white',
                                        )}
                                    >
                                        {active && (
                                            <motion.span
                                                layoutId="customer-nav-active"
                                                    className="absolute inset-0 -z-0 rounded-full bg-[#f51b75]"
                                            />
                                        )}
                                            <Icon className="relative z-10 size-4" />
                                            <span className="relative z-10">{label}</span>
                                        </Link>
                                    );
                                })}
                                <button
                                    onClick={() => router.post('/logout')}
                                    className="flex shrink-0 items-center gap-3 rounded-full px-4 py-3 text-left text-sm font-semibold text-destructive transition hover:bg-destructive/10"
                                >
                                    Logout
                                </button>
                            </nav>
                        </aside>
                        <motion.section
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="customer-luxury min-w-0 rounded-[2rem] border border-rose-100 bg-[linear-gradient(180deg,rgba(255,255,255,0.96),rgba(255,247,250,0.92))] p-6 shadow-[0_18px_60px_rgba(245,27,117,0.08)] backdrop-blur sm:p-9 lg:p-11"
                        >
                            {children}
                        </motion.section>
                    </div>
                </div>
            </div>
        </StoreLayout>
    );
}
